import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowUpRight, Lock } from "lucide-react";
import AppleLogo from "./AppleLogo";

const PLANS = [
  {
    id: "one-hour",
    title: "1 Hour Shoot",
    price: "$200",
    description:
      "Perfect for quick reels, creator content, lifestyle shots, and small business social media content.",
    perks: [
      "1–2 Edited Reel Delivered",
      { iphone: true, before: "Shot on Latest ", after: "iPhone" },
      "Fast Delivery (10 mins post shoot)",
      "Shot by Trained and Certified Creator",
      "BlinkShots Branding Included",
    ],
    buttonText: "Book 1 Hour Shoot",
    stripeUrl: "https://buy.stripe.com/fZu14ffVwd9l2HAc2k93y00",
    highlight: false,
  },
  {
    id: "three-hour",
    title: "1–3 Hour Shoot",
    price: "$350",
    description:
      "Best for extended content creation, multiple reels, personal branding, and business marketing content.",
    perks: [
      "2–5 Edited Reel Delivered",
      { iphone: true, before: "Shot on Latest ", after: "iPhone" },
      "Fast Delivery (10 mins post shoot)",
      "Shot by Trained and Certified Creator",
      "BlinkShots Branding Included",
    ],
    buttonText: "Book 1–3 Hour Shoot",
    stripeUrl: "https://buy.stripe.com/00wcMX4cOfht5TM6I093y01",
    highlight: true,
    badge: "Most Popular",
  },
  {
    id: "half-day",
    title: "Half Day Shoot",
    price: "$700",
    description:
      "Premium half-day shoot for events, brand campaigns, multiple locations, and cinematic content production.",
    perks: [
      "4–8 Edited Reel Delivered",
      { iphone: true, before: "Shot on Latest ", after: "iPhone" },
      "Fast Delivery (10 mins post shoot)",
      "Shot by Trained and Certified Creator",
      "BlinkShots Branding Included",
    ],
    buttonText: "Book Half Day Shoot",
    stripeUrl: "https://buy.stripe.com/8x214f8t43yL1Dw5DW93y02",
    highlight: false,
  },
  {
    id: "custom",
    title: "Custom Shoot",
    price: "Custom",
    priceSuffix: "Pricing",
    description:
      "Tailored creative production for events, brand campaigns, luxury productions, nightlife, and influencer collaborations.",
    perks: [
      "Custom Reel Packages",
      "Event & Brand Coverage",
      "Multi-Location Shoots",
      "Personalized Production Plan",
      "Fast Delivery Available",
    ],
    buttonText: "Contact Us",
    isCustom: true,
    highlight: false,
    badge: "Flexible",
  },
];

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (el) {
    // Use the hash hook so the Contact form auto-switches to Custom Shoot mode
    if (window.location.hash !== "#contact-custom") {
      window.history.replaceState(null, "", "#contact-custom");
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    }
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export const Pricing = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const headerRotate = useTransform(scrollYProgress, [0, 0.3], [10, 0]);

  return (
    <section
      id="pricing"
      ref={ref}
      data-testid="pricing-section"
      className="relative py-28 md:py-36 perspective-scene"
    >
      {/* ambient backgrounds */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,#100202,transparent_60%)] pointer-events-none" />
      <div
        aria-hidden
        className="absolute top-10 -left-52 h-[460px] w-[460px] rounded-full blur-[130px] opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #4ea8ff, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 -right-52 h-[460px] w-[460px] rounded-full blur-[130px] opacity-18 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #4ea8ff, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[720px] rounded-full blur-[140px] opacity-25 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #7a0000, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute top-0 left-1/3 h-[280px] w-[520px] rounded-full blur-[140px] opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #ff3d8e, transparent 70%)" }}
      />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="neon-streak top-[6%] left-[-10%] right-[-10%]" />

      <div className="relative mx-auto max-w-7xl px-5">
        <motion.div
          style={{ rotateX: headerRotate }}
          className="mb-14 md:mb-16 max-w-3xl"
        >
          <p className="mono-eyebrow mb-4" data-testid="pricing-eyebrow">
            Pricing
          </p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[1.02] tracking-tight">
            Choose Your{" "}
            <span className="text-[#E10600]">Shoot Package</span>
          </h2>
          <p className="mt-5 text-neutral-400 font-body text-base md:text-lg">
            Book your BlinkShots content session instantly with secure Stripe checkout.
          </p>
        </motion.div>

        {/* 4 cards: 1 col mobile, 2 cols tablet, 4 cols desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {PLANS.map((p, i) => (
            <PricingCard key={p.id} plan={p} index={i} />
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-2 text-xs text-neutral-500 font-mono">
          <Lock className="h-3 w-3 text-[#E10600]" />
          <span>Secure checkout powered by Stripe.</span>
        </div>
      </div>
    </section>
  );
};

const PricingCard = ({ plan, index }) => {
  const isCustom = plan.isCustom;

  const cardBg = plan.highlight
    ? "bg-[#0c0304] border-[#E10600]/40"
    : "bg-[#0a0a0a] border-white/10";

  const ActionTag = isCustom ? "button" : "a";
  const actionProps = isCustom
    ? {
        type: "button",
        onClick: (e) => {
          e.preventDefault();
          scrollToContact();
        },
      }
    : {
        href: plan.stripeUrl,
        target: "_blank",
        rel: "noopener noreferrer",
      };

  return (
    <motion.div
      initial={{ opacity: 0, y: 60, rotateX: 14 }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, margin: "-15%" }}
      transition={{ duration: 0.9, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -8 }}
      className={`group relative overflow-hidden rounded-3xl flex flex-col border ${cardBg}`}
      data-testid={`pricing-card-${plan.id}`}
    >
      {/* inner glow wash */}
      <div
        aria-hidden
        className={`absolute inset-0 pointer-events-none ${
          plan.highlight
            ? "bg-[radial-gradient(ellipse_at_top_right,rgba(225,6,0,0.25),transparent_60%)]"
            : "bg-[radial-gradient(ellipse_at_top_right,rgba(225,6,0,0.12),transparent_65%)]"
        }`}
      />
      <div
        aria-hidden
        className="absolute -top-28 -right-20 h-52 w-52 rounded-full bg-[#E10600]/20 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute -bottom-28 -left-20 h-52 w-52 rounded-full bg-[#ff3d8e]/10 blur-3xl pointer-events-none"
      />

      {plan.badge && (
        <div className="absolute top-5 right-5 z-10">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-white shadow-[0_6px_20px_rgba(225,6,0,0.5)] ${
              plan.highlight ? "bg-[#E10600]" : "bg-white/10 border border-white/20"
            }`}
          >
            {plan.badge}
          </span>
        </div>
      )}

      <div className="relative p-7 md:p-7 flex flex-col flex-1 z-10">
        <div className="flex items-center gap-3 mb-5">
          <span className="mono-eyebrow">0{index + 1}</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <h3
          className="font-display text-2xl md:text-3xl text-white tracking-tight leading-tight"
          data-testid={`pricing-title-${plan.id}`}
        >
          {plan.title}
        </h3>

        <div className="mt-4 flex items-baseline gap-2 flex-wrap">
          <p
            className="font-display text-4xl md:text-5xl text-[#E10600] leading-none"
            data-testid={`pricing-price-${plan.id}`}
            style={{
              textShadow: "0 0 24px rgba(225,6,0,0.35), 0 0 40px rgba(255,61,142,0.15)",
            }}
          >
            {plan.price}
          </p>
          <span className="mono-eyebrow text-neutral-500">
            {plan.priceSuffix || "/ shoot"}
          </span>
        </div>

        <p className="mt-4 text-sm text-neutral-400 font-body leading-relaxed">
          {plan.description}
        </p>

        <div className="mt-5 h-px bg-gradient-to-r from-[#E10600]/40 via-white/10 to-transparent" />

        <ul className="mt-5 space-y-3 flex-1">
          {plan.perks.map((perk, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-sm text-neutral-200 font-body"
              data-testid={`pricing-perk-${plan.id}-${i}`}
            >
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#E10600]/15 border border-[#E10600]/40 mt-0.5">
                <Check className="h-3 w-3 text-[#E10600]" strokeWidth={3} />
              </span>
              {typeof perk === "string" ? (
                <span>{perk}</span>
              ) : perk.iphone ? (
                <span className="inline-flex items-center gap-1 flex-wrap">
                  <span>{perk.before}</span>
                  <AppleLogo className="h-[14px] w-[14px] text-white inline-block -mt-0.5" />
                  <span className="font-medium text-white">{perk.after}</span>
                </span>
              ) : null}
            </li>
          ))}
        </ul>

        <ActionTag
          {...actionProps}
          data-testid={`pricing-cta-${plan.id}`}
          className="btn-pricing relative mt-7 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-medium text-white w-full no-underline"
        >
          <span className="relative z-10">{plan.buttonText}</span>
          <ArrowUpRight className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </ActionTag>
      </div>
    </motion.div>
  );
};

export default Pricing;
